/**
 * web/endingOverlay.ts
 * The final ending sequence overlay, split out of `web/overlays.ts`.
 *
 * It lives in its own module because adding it inline pushed
 * `overlays.ts` to 296 of the 300-line cap in AGENTS.md, leaving no
 * room for the next overlay. The ending is also the only overlay with
 * multi-step state, so it has little in common with the single-shot
 * cards next door.
 *
 * @module web/endingOverlay
 */

/// <reference lib="dom" />

import { makeBackdrop, makeCard, makeBtn, type OverlayHandle } from './overlays';
/** A single tap-to-advance panel in the ending sequence. */
export interface EndingPanel {
  /** Body copy for this panel. */
  text: string;
  /** Extra class so the closing beat and credits can be styled apart. */
  variant: 'line' | 'closing' | 'credits';
}

/**
 * Show the final ending sequence as a tappable overlay.
 *
 * Unlike {@link showIntroCutscene} there is no skip control: the ending
 * is the payoff for clearing the Star-Eater, and the panel count is
 * small enough that skipping would save nothing. The last panel's
 * button reads as a return to the star map rather than "Continue", so
 * the player is never left guessing what dismissing it does.
 *
 * @param host   Element the overlay attaches to.
 * @param panels Ordered panels; the sequence ends after the last one.
 * @param onDone Invoked once the final panel is dismissed.
 * @returns An {@link OverlayHandle} whose `destroy()` removes the overlay.
 */
export function showEndingCutscene(
  host: HTMLElement,
  panels: readonly EndingPanel[],
  onDone: () => void,
): OverlayHandle {
  const backdrop = makeBackdrop('sf-ending');
  const card = makeCard('sf-ending-card');
  const text = document.createElement('div');
  let idx = 0;
  // DECISION: completion is latched. `onDone` here navigates
  // (handleQuit -> router.back), so a double-tap on the final panel
  // would pop history twice and land the player past the star map.
  // Removing the backdrop is not enough on its own — a second click
  // already in flight still dispatches to the detached button.
  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    backdrop.remove();
    onDone();
  };
  // DECISION: guard an empty panel list rather than trusting the caller —
  // an ending that mounts a blank, undismissable card would soft-lock the
  // player on the one screen they cannot restart out of.
  if (panels.length === 0) {
    onDone();
    return { destroy: () => undefined };
  }
  const advance = makeBtn('Continue', 'sf-btn sf-btn-primary');
  const draw = (): void => {
    const panel = panels[idx] as EndingPanel;
    text.className = `sf-ending-text sf-ending-${panel.variant}`;
    text.textContent = panel.text;
    advance.textContent = idx === panels.length - 1 ? 'Return to the star map' : 'Continue';
  };
  advance.addEventListener('click', () => {
    if (finished) return;
    idx += 1;
    if (idx >= panels.length) {
      finish();
      return;
    }
    draw();
  });
  draw();
  const actions = document.createElement('div');
  actions.className = 'sf-overlay-actions';
  actions.append(advance);
  card.append(text, actions);
  backdrop.append(card);
  host.append(backdrop);
  // DECISION: teardown latches too. `destroy()` runs when the host screen
  // goes away mid-sequence; if it only removed the backdrop, a click
  // already in flight would still reach the handler and navigate on a
  // screen that no longer exists. Same reasoning as the click latch —
  // both paths have to close, not just the one the player drives.
  return {
    destroy: () => {
      finished = true;
      backdrop.remove();
    },
  };
}
